/** Member-scoped data access for the provider app: the practice roster, and one member's record. */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { trackException } from '@mdvip/shared/lib/telemetry';
import {
  type MemberDirectoryEntry,
  MemberInfoByContactIdDocument,
  MyActiveMembersDocument,
  MyIppeCalculationMembersDocument,
  MyOutstandingAwpMembersDocument,
  MyPastDueMembersDocument,
  MyPracticeMembersDocument,
  MyRecommendedOutreachMembersDocument,
  MyScholarshipMembersDocument,
  MySuspendedMembersDocument,
  MyUpcomingRenewalMembersDocument,
  type PostalAddress,
} from '@/graphql/generated/graphql';
import { priorityTags } from '@/lib/member-views/priorityTags';
import type {
  ActiveMember,
  ActiveMemberRow,
  AgingFields,
  IppeCalculationMemberRow,
  MailingAddressFields,
  MemberDirectoryRowBase,
  OutstandingAwpMemberRow,
  PastDueMemberRow,
  PracticeMember,
  RecommendedOutreachMemberRow,
  ScholarshipMemberRow,
  SuspendedMemberRow,
  UpcomingRenewalMemberRow,
} from '@/lib/members/members.types';

/**
 * The statuses a provider works with. Baked into the query key, not a parameter: the roster is one
 * cached list for the whole app, and a second status set would be a different feature.
 */
const ROSTER_STATUS = ['Active', 'Suspended'] as const;

export const myPracticeMembersKey = ['members', 'myPracticeMembers', { status: ROSTER_STATUS }] as const;

export const memberInfoByContactIdKey = (contactId: string) => ['members', 'memberInfoByContactId', contactId] as const;

export const myActiveMembersKey = ['members', 'myActiveMembers'] as const;

export const mySuspendedMembersKey = ['members', 'mySuspendedMembers'] as const;

export const myOutstandingAwpMembersKey = ['members', 'myOutstandingAwpMembers'] as const;

export const myUpcomingRenewalMembersKey = ['members', 'myUpcomingRenewalMembers'] as const;

export const myPastDueMembersKey = ['members', 'myPastDueMembers'] as const;

export const myScholarshipMembersKey = ['members', 'myScholarshipMembers'] as const;

export const myRecommendedOutreachMembersKey = ['members', 'myRecommendedOutreachMembers'] as const;

export const myIppeCalculationMembersKey = ['members', 'myIppeCalculationMembers'] as const;

/**
 * The whole roster in one unpaged call, in the server's own order (first name ascending) — the
 * selection surfaces search and filter it client-side, so re-sorting here would only fight that.
 */
export async function requestMyPracticeMembers(): Promise<PracticeMember[]> {
  const data = await gqlRequest(MyPracticeMembersDocument, { input: { status: [...ROSTER_STATUS] } });
  const { edges, totalCount } = data.myPracticeMembers;

  // Salesforce caps a single SOQL response at 2000 rows. A practice that large would silently show
  // a partial roster, and the member a provider is looking for might be in the missing tail.
  if (totalCount > edges.length) {
    trackException(new Error('myPracticeMembers roster truncated'), {
      source: 'requestMyPracticeMembers',
      totalCount: String(totalCount),
      returned: String(edges.length),
    });
  }

  return edges.map((edge) => ({
    id: edge.id,
    firstName: edge.firstName ?? null,
    lastName: edge.lastName ?? null,
    mdvipId: edge.mdvipId ?? null,
    dateOfBirth: edge.dateOfBirth ?? null,
  }));
}

/** The directory entry fields every roster read selects — what the shared row is built from. */
type DirectoryRowEdge = Pick<MemberDirectoryEntry, Exclude<keyof MemberDirectoryRowBase, 'priorityTags'>>;

/** The row every directory view shares, with each absent field as `null` and the tags derived. */
function toDirectoryRowBase(edge: DirectoryRowEdge): MemberDirectoryRowBase {
  const row = {
    id: edge.id,
    firstName: edge.firstName ?? null,
    lastName: edge.lastName ?? null,
    mdvipId: edge.mdvipId ?? null,
    dateOfBirth: edge.dateOfBirth ?? null,
    memberSince: edge.memberSince ?? null,
    phone: edge.phone ?? null,
    mobilePhone: edge.mobilePhone ?? null,
    email: edge.email ?? null,
    memberStatus: edge.memberStatus ?? null,
    lastAwpDate: edge.lastAwpDate ?? null,
    renewalDate: edge.renewalDate ?? null,
    currentAgreementPaymentSchedule: edge.currentAgreementPaymentSchedule ?? null,
    currentAgreementMsrp: edge.currentAgreementMsrp ?? null,
    autoPay: edge.autoPay ?? null,
    lastPaymentDate: edge.lastPaymentDate ?? null,
    lastPaymentAmount: edge.lastPaymentAmount ?? null,
    currentBalanceFormula: edge.currentBalanceFormula ?? null,
    billingDaysPastDue: edge.billingDaysPastDue ?? null,
    billToNationalAccount: edge.billToNationalAccount ?? null,
    nationalAccountName: edge.nationalAccountName ?? null,
    isHealthChampion: edge.isHealthChampion,
  };
  return { ...row, priorityTags: priorityTags(row) };
}

/**
 * The directory's All Active Members view: every Active and Suspended member, one unpaged call, in
 * the server's order (last name ascending). The status rule is the server's, so there's no input.
 */
export async function requestMyActiveMembers(): Promise<ActiveMemberRow[]> {
  const data = await gqlRequest(MyActiveMembersDocument);

  return data.myActiveMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    age: edge.age ?? null,
    nextBirthday: edge.nextBirthday ?? null,
    insuranceName: edge.insuranceName ?? null,
  }));
}

/**
 * The directory's Suspended Members view: every Suspended member, one unpaged call, in the server's
 * order (last name ascending). Like All Active, the status rule is the server's.
 */
export async function requestMySuspendedMembers(): Promise<SuspendedMemberRow[]> {
  const data = await gqlRequest(MySuspendedMembersDocument);

  return data.mySuspendedMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    ...toAgingFields(edge),
  }));
}

/** The past-due buckets, each absent one as `null`. */
function toAgingFields(edge: Pick<MemberDirectoryEntry, keyof AgingFields>): AgingFields {
  return {
    agingCurrent: edge.agingCurrent ?? null,
    aging30: edge.aging30 ?? null,
    aging60: edge.aging60 ?? null,
    aging90: edge.aging90 ?? null,
    aging120: edge.aging120 ?? null,
  };
}

type MailingAddressEdge = Pick<PostalAddress, 'line1' | 'line2' | 'city' | 'state' | 'zip'>;

/**
 * A mailing address as report columns. The two street lines join with ", " so each member stays on
 * one spreadsheet row; with no address on file, every column is `null`.
 */
function toMailingAddressFields(address: MailingAddressEdge | null | undefined): MailingAddressFields {
  return {
    mailingStreet: [address?.line1, address?.line2].filter(Boolean).join(', ') || null,
    mailingCity: address?.city ?? null,
    mailingState: address?.state ?? null,
    mailingZip: address?.zip ?? null,
  };
}

/**
 * The directory's Outstanding AWP view: every Active or Suspended, non-Scholarship member whose last
 * AWP is over a year old or was never recorded, one unpaged call, in the server's order (most
 * overdue first, never-recorded first of all).
 */
export async function requestMyOutstandingAwpMembers(): Promise<OutstandingAwpMemberRow[]> {
  const data = await gqlRequest(MyOutstandingAwpMembersDocument);

  return data.myOutstandingAwpMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    ...toMailingAddressFields(edge.mailingAddress),
  }));
}

/**
 * The directory's Upcoming Renewals view: every Active member renewing within the next 62 days, one
 * unpaged call, in the server's order (farthest renewal first; the dataset re-sorts it).
 */
export async function requestMyUpcomingRenewalMembers(): Promise<UpcomingRenewalMemberRow[]> {
  const data = await gqlRequest(MyUpcomingRenewalMembersDocument);

  return data.myUpcomingRenewalMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    ...toMailingAddressFields(edge.mailingAddress),
  }));
}

/**
 * The directory's Past Due view: every Active member 1–60 days past due, company-paid corporate
 * members excluded, one unpaged call, in the server's order (most days past due first). The server
 * doesn't return the corporate-billing flag for this view, so no row carries a Corp tag.
 */
export async function requestMyPastDueMembers(): Promise<PastDueMemberRow[]> {
  const data = await gqlRequest(MyPastDueMembersDocument);

  return data.myPastDueMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    ...toMailingAddressFields(edge.mailingAddress),
    ...toAgingFields(edge),
    salutation: edge.salutation ?? null,
    ccDeclineCall: edge.ccDeclineCall ?? null,
    ccDeclineLetter: edge.ccDeclineLetter ?? null,
    dunCall1: edge.dunCall1 ?? null,
    dunCall2: edge.dunCall2 ?? null,
    dunCall3: edge.dunCall3 ?? null,
    dunCall4: edge.dunCall4 ?? null,
    dunLetter1: edge.dunLetter1 ?? null,
    dunLetter2: edge.dunLetter2 ?? null,
    dunLetter3: edge.dunLetter3 ?? null,
    dunLetter4: edge.dunLetter4 ?? null,
    dunEmail1: edge.dunEmail1 ?? null,
    dunEmail2: edge.dunEmail2 ?? null,
    dunEmail3: edge.dunEmail3 ?? null,
    dunEmail4: edge.dunEmail4 ?? null,
    lastCancelReason: edge.lastCancelReason ?? null,
  }));
}

/**
 * The directory's Scholarship Members view: every Active or Suspended member on a Scholarship billing
 * schedule, one unpaged call, in the server's order (last name ascending).
 */
export async function requestMyScholarshipMembers(): Promise<ScholarshipMemberRow[]> {
  const data = await gqlRequest(MyScholarshipMembersDocument);

  return data.myScholarshipMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    ...toMailingAddressFields(edge.mailingAddress),
    nextBirthday: edge.nextBirthday ?? null,
    insuranceName: edge.insuranceName ?? null,
    scholarshipEndDate: edge.scholarshipEndDate ?? null,
  }));
}

/**
 * The directory's Recommended Outreach view: every Active member renewing within the next 90 days who
 * looks likely to cancel, one unpaged call, in the server's order (last name ascending; the dataset
 * re-sorts it).
 */
export async function requestMyRecommendedOutreachMembers(): Promise<RecommendedOutreachMemberRow[]> {
  const data = await gqlRequest(MyRecommendedOutreachMembersDocument);

  return data.myRecommendedOutreachMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    salutation: edge.salutation ?? null,
  }));
}

/**
 * The directory's IPPE Calculation view: every Active or Suspended member who is 64 and turns 65
 * within the next 90 days, one unpaged call, in the server's order (last name ascending; the dataset
 * re-sorts it). The server returns only name, email, home phone, DOB, status and salutation for this
 * view, so no row carries an MDVIP ID or a Corp tag.
 */
export async function requestMyIppeCalculationMembers(): Promise<IppeCalculationMemberRow[]> {
  const data = await gqlRequest(MyIppeCalculationMembersDocument);

  return data.myIppeCalculationMembers.edges.map((edge) => ({
    ...toDirectoryRowBase(edge),
    salutation: edge.salutation ?? null,
    suggestedStartDate: edge.suggestedStartDate ?? null,
    suggestedEndDate: edge.suggestedEndDate ?? null,
  }));
}

/**
 * One member's record.
 *
 * `null` only on a schema-level nullable response the resolver doesn't actually produce today —
 * `memberInfoByContactId` throws a `NOT_FOUND`-coded GraphQL error instead when the contact id no
 * longer resolves (verified against `MemberResolver.fetchAndGate`), so that case surfaces through
 * `useActiveMember`'s `error`, not through this returning `null`. Kept here anyway: the schema
 * still marks the field nullable, and this mapper shouldn't assume a resolver detail that could
 * change without a schema change to force the point.
 */
export async function requestMemberInfoByContactId(contactId: string): Promise<ActiveMember | null> {
  const data = await gqlRequest(MemberInfoByContactIdDocument, { contactId });
  const member = data.memberInfoByContactId;
  if (!member) return null;

  return {
    id: member.id,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    mdvipId: member.mdvipId ?? null,
    dateOfBirth: member.dateOfBirth ?? null,
  };
}
